import React, { useState, useMemo } from 'react';
import { useAppDispatch } from 'src/store';
import { NewProjectDefinitionFormState, setFormState } from 'src/app/projects/views/new-project-definition/state/newProjectDefinitionSlice';
import Input from 'src/shared/components/input/Input';
import { availableAreaColors } from 'src/shared/constants/colors';
import { PrimaryButton } from 'src/shared/components/button/Button';
import commonStyles from 'src/app/projects/views/new-project-definition/NewProjectDefinition.module.scss';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import { X as ClearOutlinedIcon } from 'lucide-react';
import styles from './SampleGroups.module.scss';
import useProjectDefinition from '../../../../hooks/useProjectDefinition';

const SampleGroups = () => {
    const dispatch = useAppDispatch();
    const { formState } = useProjectDefinition();
    const { sampleGroups } = formState;
    const [sampleGroupValue, setSampleGroupValue] = useState('');

    const onFormChange = (fragment: Partial<NewProjectDefinitionFormState>) => {
        dispatch(
            setFormState({
                ...fragment,
            })
        );
    };

    // Check if current input is a duplicate (case-insensitive)
    const isDuplicate = useMemo(() => {
        const trimmedValue = sampleGroupValue.trim();
        if (!trimmedValue) {
            return false;
        }
        return sampleGroups.some(group => group.toLowerCase() === trimmedValue.toLowerCase());
    }, [sampleGroupValue, sampleGroups]);

    const isMaxReached = sampleGroups.length >= 8;
    const isDisabled = !sampleGroupValue.trim() || isDuplicate || isMaxReached;

    const addSampleGroup = () => {
        const trimmedValue = sampleGroupValue.trim();

        if (!trimmedValue || isDuplicate || isMaxReached) {
            return;
        }

        onFormChange({
            sampleGroups: [...sampleGroups, trimmedValue],
        });
        setSampleGroupValue('');
    };

    const removeSampleGroup = (value: string) => {
        onFormChange({
            sampleGroups: [...sampleGroups].filter(item => item !== value),
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && sampleGroupValue.trim() && !e.shiftKey) {
            e.preventDefault();
            addSampleGroup();
        }
    };

    return (
        <div className={commonStyles.formSection}>
            <div className={commonStyles.formInputSection}>
                <div className={commonStyles.formInputSectionTitle}>
                    Sample groups <QuestionButtonHelp type='api' slug='project-definition-sample-groups' />
                </div>
                <div className={styles.addSampleGroup}>
                    <span
                        className={styles.colorIndicator}
                        style={
                            {
                                '--sample-group-input-area-indicator-color': availableAreaColors[sampleGroups.length],
                            } as React.CSSProperties
                        }
                    ></span>
                    <div className={styles.inputWrapper}>
                        <Input
                            className={styles.sampleGroupInput}
                            value={sampleGroupValue}
                            onChange={event => setSampleGroupValue(event.target.value)}
                            onKeyDown={handleKeyDown}
                            maxLength={25}
                            placeholder='Type sample group name (press Enter to add)'
                        />
                    </div>

                    {!isMaxReached && (
                        <div className={styles.addButtonWrapper}>
                            <PrimaryButton className={commonStyles.addButton} onClick={addSampleGroup} disabled={isDisabled}>
                                <span>+</span> ADD
                            </PrimaryButton>
                            {isDuplicate && <span className={styles.duplicateHint}>This sample group already exists</span>}
                        </div>
                    )}
                </div>
                {sampleGroups.length > 0 && <div className={commonStyles.maximumText}>{sampleGroups.length}/8 sample groups added</div>}
                <div className={styles.sampleGroups}>
                    {sampleGroups.map((entry, index) => {
                        return (
                            <div key={index} className={styles.sampleGroupTag}>
                                <div className={styles.sampleGroupTagColor} style={{ backgroundColor: availableAreaColors[index] }}></div>
                                <div className={styles.sampleGroupTagValue}>{entry}</div>
                                <ClearOutlinedIcon onClick={() => removeSampleGroup(entry)} />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default SampleGroups;
